import type { GameState, Move, MoveMode, Piece, Shape, Square } from "./types";
import { inBounds, sameSquare, squareKey, isOrthogonal } from "./coords";
import { ROUND_STEP, SQUARE_STEP, TRIANGLE_STEP } from "./constants";

const ORTHO_DIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

const DIAG_DIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

/**
 * Knight-style "flying" offsets for Triangles (1,2). Per the Boissière/Fulke historical rules
 * a Triangle's secondary move is a chess-knight's hop — non-capturing, jumps over any piece
 * on the L-path.
 */
const TRIANGLE_KNIGHT_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [1, 2], [-1, 2], [1, -2], [-1, -2],
  [2, 1], [-2, 1], [2, -1], [-2, -1],
];

/**
 * Extended knight offsets for Squares (1,3) — same shape as the Triangle's knight move but
 * with a 3-step long arm. Also non-capturing and leaping.
 */
const SQUARE_KNIGHT_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [1, 3], [-1, 3], [1, -3], [-1, -3],
  [3, 1], [-3, 1], [3, -1], [-3, -1],
];

function pieceAt(state: GameState, sq: Square): Piece | undefined {
  return state.board.get(squareKey(sq));
}

/** Returns the effective movement shape for a move (resolves Pyramid via `mode`). */
export function effectiveShape(piece: Piece, mode?: MoveMode): Shape | null {
  if (piece.shape === "pyramid") {
    if (!mode) return null;
    // If the Pyramid is layered (Fulke V1), the chosen mode must still have an uncaptured
    // layer of that shape. Layer-less pyramids (legacy / tests) are treated as fully able.
    if (piece.layers && !pyramidHasMode(piece, mode)) return null;
    return mode;
  }
  if (mode && mode !== piece.shape) return null;
  return piece.shape;
}

/**
 * Fulke V1: the Pyramid (king) loses the ability to move in a given shape once every layer of
 * that shape has been captured. White's king starts with 2 round / 2 triangle / 2 square layers;
 * Black's with 1 round / 2 triangle / 2 square.
 */
export function pyramidHasMode(piece: Piece, mode: MoveMode): boolean {
  if (piece.shape !== "pyramid" || !piece.layers) return false;
  return piece.layers.some((l) => !l.captured && l.shape === mode);
}

export function pyramidAvailableModes(piece: Piece): MoveMode[] {
  if (piece.shape !== "pyramid") return [];
  // Layer-less pyramid (legacy / tests): treat as having all modes.
  if (!piece.layers) return ["round", "triangle", "square"];
  const set = new Set<MoveMode>();
  for (const l of piece.layers) if (!l.captured) set.add(l.shape);
  return ["round", "triangle", "square"].filter((m) => set.has(m as MoveMode)) as MoveMode[];
}

export interface ReachOpts {
  /**
   * If true, only the *capturing* moves count — i.e., the standard step. Non-capturing
   * knight-fly moves are excluded. Capture-reach (Encounter, Assault) uses this so a
   * piece can't "fly" onto an enemy.
   */
  forCapture?: boolean;
}

/**
 * Can `from`→`to` be reached by the given shape?
 * Treats `to` as the destination regardless of its contents (caller decides empty vs capturable).
 *
 * Fulke 1563 V1 movement ("first kynde"), with one house-rule adjustment:
 *   - Round:    exactly 1 step in ANY direction (8-way king's-step). House rule — Fulke's text
 *               specifies diagonal only, but the diagonal-only Round is color-locked, which
 *               removes half the board from any Round's reach. We allow orthogonal too so
 *               Rounds can capture across colors and reach more squares per move.
 *   - Triangle: standard = exactly 2 steps orthogonally, path must be clear. Flying =
 *               chess-knight hop (1,2)/(2,1), leaps over pieces, non-capturing only.
 *   - Square:   standard = exactly 3 steps orthogonally, path must be clear. Flying =
 *               extended-knight (1,3)/(3,1), leaps, non-capturing only.
 *   - Pyramid:  delegates to one of the above via `effectiveShape` / `mode`. The mode is
 *               only legal if at least one uncaptured layer carries that shape.
 */
export function canShapeReach(
  state: GameState,
  shape: Shape,
  from: Square,
  to: Square,
  opts: ReachOpts = {},
): boolean {
  if (sameSquare(from, to)) return false;
  if (!inBounds(to)) return false;

  const df = to.file - from.file;
  const dr = to.rank - from.rank;
  const adf = Math.abs(df);
  const adr = Math.abs(dr);

  switch (shape) {
    case "round": {
      // Exactly 1 step in any direction (king's-step). House rule, not strict Fulke.
      if (adf > ROUND_STEP || adr > ROUND_STEP) return false;
      return adf + adr > 0;
    }
    case "triangle": {
      // Standard: 2 steps orthogonal, path must be clear.
      if (isOrthogonal(from, to) && adf + adr === TRIANGLE_STEP) {
        return pathClear(state, from, to, TRIANGLE_STEP);
      }
      // Flying knight-move: non-capturing only.
      if (!opts.forCapture && isKnightOffset(adf, adr, 1, 2)) {
        return true;
      }
      return false;
    }
    case "square": {
      // Standard: 3 steps orthogonal, path must be clear.
      if (isOrthogonal(from, to) && adf + adr === SQUARE_STEP) {
        return pathClear(state, from, to, SQUARE_STEP);
      }
      // Flying extended-knight: non-capturing only.
      if (!opts.forCapture && isKnightOffset(adf, adr, 1, 3)) {
        return true;
      }
      return false;
    }
    case "pyramid":
      // Pyramid must choose a mode — handled by `effectiveShape`. Caller should not pass "pyramid" here.
      return false;
  }
}

function isKnightOffset(adf: number, adr: number, shortArm: number, longArm: number): boolean {
  return (adf === shortArm && adr === longArm) || (adf === longArm && adr === shortArm);
}

function pathClear(state: GameState, from: Square, to: Square, totalSteps: number): boolean {
  const df = to.file - from.file;
  const dr = to.rank - from.rank;
  const stepF = Math.sign(df);
  const stepR = Math.sign(dr);
  for (let i = 1; i < totalSteps; i++) {
    const inter: Square = { file: from.file + stepF * i, rank: from.rank + stepR * i };
    if (state.board.has(squareKey(inter))) return false;
  }
  return true;
}

/** Generates all legal destination squares for the given piece (empty squares only — for movement, not capture). */
export function legalDestinations(state: GameState, from: Square, mode?: MoveMode): Square[] {
  const piece = pieceAt(state, from);
  if (!piece) return [];

  const shape = effectiveShape(piece, mode);
  if (!shape) return [];

  const result: Square[] = [];

  const tryAdd = (to: Square) => {
    if (!inBounds(to)) return;
    if (state.board.has(squareKey(to))) return; // movement requires empty destination
    if (canShapeReach(state, shape, from, to)) result.push(to);
  };

  if (shape === "round") {
    // 8-way king's-step (orthogonal + diagonal, both 1 square).
    for (const [df, dr] of ORTHO_DIRS) {
      tryAdd({ file: from.file + df * ROUND_STEP, rank: from.rank + dr * ROUND_STEP });
    }
    for (const [df, dr] of DIAG_DIRS) {
      tryAdd({ file: from.file + df * ROUND_STEP, rank: from.rank + dr * ROUND_STEP });
    }
  } else if (shape === "triangle") {
    // Standard 2-step orthogonal candidates.
    for (const [df, dr] of ORTHO_DIRS) {
      tryAdd({ file: from.file + df * TRIANGLE_STEP, rank: from.rank + dr * TRIANGLE_STEP });
    }
    // Flying knight hops.
    for (const [df, dr] of TRIANGLE_KNIGHT_OFFSETS) {
      tryAdd({ file: from.file + df, rank: from.rank + dr });
    }
  } else if (shape === "square") {
    // Standard 3-step orthogonal candidates.
    for (const [df, dr] of ORTHO_DIRS) {
      tryAdd({ file: from.file + df * SQUARE_STEP, rank: from.rank + dr * SQUARE_STEP });
    }
    // Flying extended-knight hops.
    for (const [df, dr] of SQUARE_KNIGHT_OFFSETS) {
      tryAdd({ file: from.file + df, rank: from.rank + dr });
    }
  }

  return result;
}

/**
 * Generates legal destinations across all pyramid movement modes.
 * Returns an array of {to, mode} entries. Used by UI for highlighting and by the bot for move enumeration.
 */
export function legalDestinationsAllModes(state: GameState, from: Square): Array<{ to: Square; mode?: MoveMode }> {
  const piece = pieceAt(state, from);
  if (!piece) return [];

  if (piece.shape === "pyramid") {
    const out: Array<{ to: Square; mode: MoveMode }> = [];
    for (const mode of pyramidAvailableModes(piece)) {
      for (const to of legalDestinations(state, from, mode)) {
        out.push({ to, mode });
      }
    }
    return out;
  }

  return legalDestinations(state, from).map((to) => ({ to }));
}

/** True if the side has any legal move available. */
export function hasAnyLegalMove(state: GameState, side: "white" | "black"): boolean {
  for (const [key, piece] of state.board) {
    if (piece.side !== side) continue;
    const sq = key.split(",").map(Number) as [number, number];
    const from: Square = { file: sq[0], rank: sq[1] };
    if (legalDestinationsAllModes(state, from).length > 0) return true;
  }
  return false;
}

/** Validates a Move object against the current state. Returns null if valid, else an error string. */
export function validateMove(state: GameState, move: Move): string | null {
  const piece = pieceAt(state, move.from);
  if (!piece) return "no piece at source square";
  if (piece.side !== state.toMove) return `it is ${state.toMove}'s turn`;
  if (state.victory) return "game already won";

  const shape = effectiveShape(piece, move.mode);
  if (!shape) {
    if (piece.shape === "pyramid") return "pyramid move requires a mode";
    return "invalid move mode for piece";
  }

  if (state.board.has(squareKey(move.to))) return "destination is occupied (use a capture instead)";
  if (!canShapeReach(state, shape, move.from, move.to)) return "destination is not reachable by this piece";

  return null;
}

/** Enumerate every possible (from, move) for the side to move. Used by bot. */
export function allLegalMoves(state: GameState): Move[] {
  const moves: Move[] = [];
  for (const [key, piece] of state.board) {
    if (piece.side !== state.toMove) continue;
    const [file, rank] = key.split(",").map(Number) as [number, number];
    const from: Square = { file, rank };
    for (const opt of legalDestinationsAllModes(state, from)) {
      moves.push({ from, to: opt.to, ...(opt.mode ? { mode: opt.mode } : {}) });
    }
  }
  return moves;
}
